import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { createFileRoute } from '@tanstack/react-router';
import {
  Body,
  Button,
  Caption,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  Row,
  Screen,
  ScreenBody,
  Select,
  Stack,
  Textarea,
} from '@leanin/ui';
import { CountryPicker } from '@/components/CountryPicker';
import { BodyWithAside } from '@/components/page-shell';
import { useSubmitNetworkLeaderApplication } from '@/lib/mutations/groups';

export const Route = createFileRoute('/_authed/networks/new')({
  component: NetworkApplyPage,
});

const REGION_VALUES = [
  { value: 'EMEA',            key: 'emea' },
  { value: 'Americas',        key: 'americas' },
  { value: 'APAC',            key: 'apac' },
  { value: 'Global Affinity', key: 'globalAffinity' },
] as const;

const COMMUNITY_SIZE_VALUES = ['50-100', '100-250', '250+'] as const;

type Errors = Partial<Record<
  'firstName' | 'lastName' | 'email' | 'country' | 'region' |
  'proposedName' | 'city' | 'whyCreate' | 'eventExperience' | 'leadershipPlan',
  string
>>;

// Single-line text entry that expands on Cmd/Ctrl+Enter, auto-resizing to fit content.
function ExpandableInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = value.slice(0, start) + '\n' + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        if (!ref.current) return;
        ref.current.selectionStart = ref.current.selectionEnd = start + 1;
        autoResize();
      });
    }
  };

  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      onChange={(e) => { onChange(e.target.value); autoResize(); }}
      style={{ resize: 'none', overflow: 'hidden' }}
    />
  );
}

function NetworkApplyPage() {
  const tn = useTranslations('Networks');

  const regionOptions = REGION_VALUES.map(({ value, key }) => ({
    value,
    label: tn(`apply.regions.${key}`),
  }));
  const communitySizeOptions = COMMUNITY_SIZE_VALUES.map((value) => ({
    value,
    label: tn(`apply.communitySize.${value}`),
  }));
  const requirements = (['req1', 'req2', 'req3'] as const).map((k) =>
    tn(`apply.requirements.${k}`),
  );
  const nextSteps = (['step1', 'step2', 'step3'] as const).map((k) =>
    tn(`apply.nextSteps.${k}`),
  );

  // YOUR DETAILS
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [email, setEmail]           = useState('');
  const [jobTitle, setJobTitle]     = useState('');
  const [countryCode, setCountryCode] = useState<string | undefined>(undefined);
  const [region, setRegion]         = useState('');
  const [phone, setPhone]           = useState('');

  // YOUR COMMUNITY
  const [circles, setCircles]               = useState('');
  const [otherCommunity, setOtherCommunity] = useState('');
  const [communitySize, setCommunitySize]   = useState('');

  // YOUR NETWORK
  const [proposedName, setProposedName] = useState('');
  const [city, setCity]                 = useState('');

  // YOUR MOTIVATION
  const [whyCreate, setWhyCreate]           = useState('');
  const [eventExperience, setEventExperience] = useState('');
  const [leadershipPlan, setLeadershipPlan] = useState('');

  const [errors, setErrors]     = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const submitApplication = useSubmitNetworkLeaderApplication();

  const onSubmit = () => {
    const required = tn('apply.required');
    const next: Errors = {};
    if (!firstName.trim())       next.firstName       = required;
    if (!lastName.trim())        next.lastName        = required;
    if (!email.trim())           next.email           = required;
    if (!countryCode)            next.country         = required;
    if (!region)                 next.region          = required;
    if (!proposedName.trim())    next.proposedName    = required;
    if (!city.trim())            next.city            = required;
    if (!whyCreate.trim())       next.whyCreate       = required;
    if (!eventExperience.trim()) next.eventExperience = required;
    if (!leadershipPlan.trim())  next.leadershipPlan  = required;
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitError(null);
    submitApplication.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
        ...(countryCode ? { countryCode } : {}),
        region: region as 'EMEA' | 'Americas' | 'APAC' | 'Global Affinity',
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(circles.trim() ? { circles: circles.trim() } : {}),
        ...(otherCommunity.trim() ? { otherCommunity: otherCommunity.trim() } : {}),
        ...(communitySize ? { communitySize } : {}),
        proposedName: proposedName.trim(),
        city: city.trim(),
        whyCreate: whyCreate.trim(),
        eventExperience: eventExperience.trim(),
        leadershipPlan: leadershipPlan.trim(),
      },
      {
        onSuccess: () => setSubmitted(true),
        onError: () => setSubmitError(tn('apply.submitError')),
      },
    );
  };

  if (submitted) {
    return (
      <Screen>
        <ScreenBody gap="xl">
          <Stack gap="sm">
            <Eyebrow tone="accent">{tn('apply.success.eyebrow')}</Eyebrow>
            <Heading level={1}>{tn('apply.success.title', { name: firstName })}</Heading>
            <Body size="lg" color="mute">
              {tn('apply.success.body')}
            </Body>
          </Stack>
          <Card pad="spacious" style={{ maxWidth: 560 }}>
            <Stack gap="lg">
              <Eyebrow tone="accent">{tn('apply.success.nextStepsTitle')}</Eyebrow>
              <Stack gap="md">
                {nextSteps.map((step, i) => (
                  <Row key={i} gap="md" align="flex-start">
                    <span
                      style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 'var(--radius-full)',
                        background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                        color: 'var(--primary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        font: 'var(--type-label-sm)',
                      }}
                    >
                      {i + 1}
                    </span>
                    <Body size="md">{step}</Body>
                  </Row>
                ))}
              </Stack>
            </Stack>
          </Card>
        </ScreenBody>
      </Screen>
    );
  }

  return (
    <Screen>
      <ScreenBody gap="xl">

        {/* ── Programme intro ────────────────────────────────────────────── */}
        <Stack gap="sm">
          <Eyebrow>{tn('apply.programEyebrow')}</Eyebrow>
          <Heading level={1}>{tn('apply.heading')}</Heading>
          <Body size="lg">
            {tn('apply.subtitle')}
          </Body>
        </Stack>

        <div
          style={{
            display: 'grid',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid; no Box prop for grid containers
            gap: 'var(--space-lg)',
            gridTemplateColumns: '1fr',
          }}
        >
          <Card pad="spacious">
            <Stack gap="lg">
              <Stack gap="md">
                <Stack gap="xs">
                  <Heading level={5}>{tn('apply.missionTitle')}</Heading>
                  <Body size="md" color="mute">
                    {tn('apply.missionBody')}
                  </Body>
                </Stack>
                <Stack gap="xs">
                  <Heading level={5}>{tn('apply.visionTitle')}</Heading>
                  <Body size="md" color="mute">
                    {tn('apply.visionBody')}
                  </Body>
                </Stack>
              </Stack>
              <div style={{ borderTop: 'var(--border-hairline) solid var(--border)' }} />
              <Stack gap="sm">
                <Heading level={5}>{tn('apply.whatIsTitle')}</Heading>
                <Body size="md" color="mute">
                  {tn('apply.whatIsBody')}
                </Body>
              </Stack>
            </Stack>
          </Card>
        </div>

        {/* ── Two-column form + sidebar ───────────────────────────────────── */}
        <BodyWithAside
          aside={
            <Stack gap="lg">

              {/* Requirements */}
              <Card pad="default">
                <Stack gap="md">
                  <Stack gap="xs">
                    <Eyebrow>{tn('apply.beforeYouApply')}</Eyebrow>
                    <Eyebrow tone="accent">{tn('apply.requirementsTitle')}</Eyebrow>
                  </Stack>
                  <Body size="sm" color="mute">
                    {tn('apply.requirementsIntro')}
                  </Body>
                  <Stack gap="sm">
                    {requirements.map((req, i) => (
                      <Row key={i} gap="sm" align="flex-start">
                        <span
                          style={{
                            flexShrink: 0,
                            width: 14,
                            height: 14,
                            marginTop: 0,
                            color: 'var(--primary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {I.check}
                        </span>
                        <Body size="sm">{req}</Body>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>

              {/* What happens next */}
              <Card pad="default">
                <Stack gap="md">
                  <Eyebrow tone="accent">{tn('apply.nextStepsTitle')}</Eyebrow>
                  <Stack gap="md">
                    {nextSteps.map((step, i) => (
                      <Row key={i} gap="md" align="flex-start">
                        <span
                          style={{
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            borderRadius: 'var(--radius-full)',
                            background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                            color: 'var(--primary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            font: 'var(--type-label-sm)',
                          }}
                        >
                          {i + 1}
                        </span>
                        <Body size="sm" color="mute">{step}</Body>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>

            </Stack>
          }
        >
          {/* ── MAIN COLUMN ─────────────────────────────────────────────── */}
          <Stack gap="lg">

            {/* YOUR DETAILS */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tn('apply.detailsEyebrow')}</Eyebrow>
                  <Eyebrow tone="accent">{tn('apply.detailsTitle')}</Eyebrow>
                </Stack>
                <Row gap="md" align="flex-start">
                  <Field label={tn('apply.firstName')} required error={errors.firstName} style={{ flex: 1 }}>
                    <Input
                      placeholder={tn('apply.firstName')}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label={tn('apply.lastName')} required error={errors.lastName} style={{ flex: 1 }}>
                    <Input
                      placeholder={tn('apply.lastName')}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </Field>
                </Row>
                <Field label={tn('apply.emailLabel')} required error={errors.email}>
                  <Input
                    type="email"
                    placeholder={tn('apply.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </Field>
                <Field label={tn('apply.jobTitle')}>
                  <Input
                    placeholder={tn('apply.jobTitlePlaceholder')}
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    autoComplete="organization-title"
                  />
                </Field>
                <Row gap="md" align="flex-start">
                  <Field label={tn('apply.country')} required error={errors.country} style={{ flex: 1 }}>
                    <CountryPicker value={countryCode} onChange={setCountryCode} />
                  </Field>
                  <Field
                    label={tn('apply.region')}
                    required
                    error={errors.region}
                    style={{ flex: 1 }}
                  >
                    <Select
                      placeholder={tn('apply.regionPlaceholder')}
                      value={region}
                      options={regionOptions}
                      onChange={(e) => setRegion(e.target.value)}
                    />
                  </Field>
                </Row>
                <Field label={tn('apply.phone')}>
                  <Input
                    type="tel"
                    placeholder={tn('apply.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR COMMUNITY */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tn('apply.communityEyebrow')}</Eyebrow>
                  <Eyebrow tone="accent">{tn('apply.communityTitle')}</Eyebrow>
                </Stack>
                <Field
                  label={tn('apply.circlesLabel')}
                  description={tn('apply.circlesHelp')}
                >
                  <ExpandableInput
                    placeholder={tn('apply.circlesPlaceholder')}
                    value={circles}
                    onChange={setCircles}
                  />
                </Field>
                <Field
                  label={tn('apply.otherCommunityLabel')}
                  description={tn('apply.otherCommunityHelp')}
                >
                  <ExpandableInput
                    placeholder={tn('apply.otherCommunityPlaceholder')}
                    value={otherCommunity}
                    onChange={setOtherCommunity}
                  />
                </Field>
                <Field
                  label={tn('apply.communitySizeLabel')}
                  description={tn('apply.communitySizeHelp')}
                >
                  <Select
                    placeholder={tn('apply.communitySizePlaceholder')}
                    value={communitySize}
                    options={[{ value: '', label: tn('apply.communitySize.preferNotToSay') }, ...communitySizeOptions]}
                    onChange={(e) => setCommunitySize(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR NETWORK */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tn('apply.networkEyebrow')}</Eyebrow>
                  <Eyebrow tone="accent">{tn('apply.networkTitle')}</Eyebrow>
                </Stack>
                <Field
                  label={tn('apply.proposedNameLabel')}
                  required
                  description={tn.rich('apply.proposedNameHelp', {
                    strong: (c) => <strong>{c}</strong>,
                    br: () => <br />,
                  })}
                  error={errors.proposedName}
                >
                  <Input
                    placeholder={tn('apply.proposedNamePlaceholder')}
                    value={proposedName}
                    onChange={(e) => setProposedName(e.target.value)}
                  />
                </Field>
                <Field
                  label={tn('apply.cityLabel')}
                  required
                  description={tn('apply.cityHelp')}
                  error={errors.city}
                >
                  <Input
                    placeholder={tn('apply.cityPlaceholder')}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR MOTIVATION */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tn('apply.motivationEyebrow')}</Eyebrow>
                  <Eyebrow tone="accent">{tn('apply.motivationTitle')}</Eyebrow>
                </Stack>
                <Field
                  label={tn('apply.whyCreateLabel')}
                  required
                  description={tn('apply.whyCreateHelp')}
                  error={errors.whyCreate}
                >
                  <Textarea
                    rows={5}
                    value={whyCreate}
                    onChange={(e) => setWhyCreate(e.target.value)}
                  />
                </Field>
                <Field
                  label={tn('apply.eventExperienceLabel')}
                  required
                  description={tn('apply.eventExperienceHelp')}
                  error={errors.eventExperience}
                >
                  <Textarea
                    rows={4}
                    value={eventExperience}
                    onChange={(e) => setEventExperience(e.target.value)}
                  />
                </Field>
                <Field
                  label={tn('apply.leadershipPlanLabel')}
                  required
                  description={tn('apply.leadershipPlanHelp')}
                  error={errors.leadershipPlan}
                >
                  <Textarea
                    rows={4}
                    value={leadershipPlan}
                    onChange={(e) => setLeadershipPlan(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {submitError !== null && (
              <Body size="sm" color="destructive">{submitError}</Body>
            )}
            <Row justify="space-between" align="center">
              <Caption>{tn('apply.requiredFields')}</Caption>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                loading={submitApplication.isPending}
              >
                {tn('apply.submit')}
              </Button>
            </Row>
          </Stack>

        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
